import { Badge, Button, Flex } from "@mantine/core";
import { DataTableColumn } from "mantine-datatable";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";

import { DatabaseMetadataPopover } from "../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import NumberProgressSlider from "../../../components/NumberProgressSlider.tsx";
import Table from "../../../components/Table/Table.tsx";
import { TableData, TableRow } from "../../../components/Table/types.ts";
import { AppRoute } from "../../../constants.ts";
import {
  CustomerFreezeStatus,
  LimitStatus,
} from "../../../graphql/generated.ts";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { Customer } from "../../../types/customer.ts";
import { Limit } from "../../../types/limit.ts";
import { toLocalDate } from "../../../utils/date.ts";
import { toHumanReadable } from "../../../utils/string.ts";
import { isBababosTenant } from "../../../utils/user.ts";
import { useStyles } from "../CustomerDatabasePage.styles.ts";

interface Props {
  customers: Customer[] | null | undefined;
  limits: Limit[] | null | undefined;
}

export const useCustomersTableData = ({
  customers,
  limits,
}: Props): TableData => {
  const { classes } = useStyles();

  const { user } = useCurrentUserContext();

  const renderFreezeStatusColumn = useCallback((item: Customer) => {
    if (item.freezeStatus === CustomerFreezeStatus.Active) {
      return (
        <Badge color="red" size="sm" radius="xs">
          Freeze
        </Badge>
      );
    }
    return (
      <Badge color="green" size="sm" radius="xs">
        Active
      </Badge>
    );
  }, []);

  const renderLimitStatusColumn = useCallback((item: Limit) => {
    let backgroundColor = "red";
    if (item.status === LimitStatus.Active) {
      backgroundColor = "green";
    } else if (item.status === LimitStatus.Inactive) {
      backgroundColor = "grey";
    }
    return (
      <Badge color={backgroundColor} size="sm" radius="xs">
        {toHumanReadable(item.status)}
      </Badge>
    );
  }, []);

  const renderLimitsColumn = useCallback(
    (item: Customer) => {
      const customerLimits = limits?.filter((l) => l.customerId === item.id);
      if (!customerLimits?.length) {
        return null;
      }
      return (
        <Table
          mih={0}
          minWidth="100%"
          highlightOnHover={false}
          styles={{ header: { zIndex: 0, color: "red" } }}
          columns={[
            {
              accessor: "id",
              hidden: true,
            },
            {
              accessor: "limitName",
              title: "Limit Name",
              width: 250,
            },
            {
              accessor: "status",
            },
            {
              accessor: "totalPaid",
              title: "Total Paid",
              width: 500,
            },
            {
              accessor: "limitActiveDate",
              title: "Limit Active Date",
            },
            {
              accessor: "olExpiredDate",
              title: "OL Expired Date",
            },
          ]}
          rows={customerLimits.map((cl) => ({
            id: cl.id,
            limitName: cl.limitName,
            status: renderLimitStatusColumn(cl),
            totalPaid: (
              <NumberProgressSlider
                value1={cl.totalPaid}
                value2={cl.totalLimit}
              />
            ),
            limitActiveDate: toLocalDate(cl.activeDate),
            olExpiredDate: toLocalDate(
              cl.limitRequest?.offerLetter?.olExpiredDate
            ),
          }))}
        />
      );
    },
    [limits, renderLimitStatusColumn]
  );

  const renderActionsColumn = useCallback(
    (item: Customer) => {
      return (
        <Flex gap={10}>
          {isBababosTenant(user) && (
            <Button variant="outlineBlue" size="xs" disabled={!item.clikDataId}>
              <Link
                className={classes.link}
                to={`${AppRoute.ClikData}/${item.clikDataId}`}
              >
                Risk Management
              </Link>
            </Button>
          )}
          <Button variant="filledBlue" size="xs">
            <Link
              className={classes.link}
              to={`${AppRoute.ActivityManagement}/${item.id}`}
            >
              AR Management
            </Link>
          </Button>
        </Flex>
      );
    },
    [classes.link, user]
  );

  const getRow = useCallback(
    (item: Customer): TableRow => {
      return {
        id: item.id,
        name: item.name,
        salesName: item.salesName,
        salesTeamName: item.salesTeamName,
        freezeStatus: renderFreezeStatusColumn(item),
        limits: renderLimitsColumn(item),
        actions: renderActionsColumn(item),
      };
    },
    [renderActionsColumn, renderFreezeStatusColumn, renderLimitsColumn]
  );

  const rows = useMemo(() => {
    return customers?.map(getRow);
  }, [customers, getRow]);

  const getColumns = useCallback((): DataTableColumn<TableRow>[] => {
    let baseColumns: DataTableColumn<TableRow>[] = [
      {
        accessor: "id",
        hidden: true,
      },
      {
        accessor: "name",
        title: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "Customer",
              fieldName: "name",
            }}
          >
            Name
          </DatabaseMetadataPopover>
        ),
        sortable: true,
        width: 250,
      },
      {
        accessor: "salesName",
        title: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "Customer",
              fieldName: "salesName",
            }}
          >
            Sales Name
          </DatabaseMetadataPopover>
        ),
        sortable: true,
      },
      {
        accessor: "salesTeamName",
        title: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "Customer",
              fieldName: "salesTeamName",
            }}
          >
            Sales Team Name
          </DatabaseMetadataPopover>
        ),
        sortable: true,
      },
      {
        accessor: "freezeStatus",
        title: (
          <DatabaseMetadataPopover
            databaseMetadata={{
              tableName: "Customer",
              fieldName: "freezeStatus",
            }}
          >
            Customer Status
          </DatabaseMetadataPopover>
        ),
        sortable: true,
      },
    ];
    if (isBababosTenant(user)) {
      baseColumns = [
        ...baseColumns,
        {
          accessor: "limits",
          title: "Limits",
        },
      ];
    }
    return [
      ...baseColumns,
      {
        accessor: "actions",
        title: "Actions",
      },
    ];
  }, [user]);

  return [getColumns(), rows];
};
